import discordConfig from '@/Discord/discord_config.json';

/**
 * DiscordRichPresenceManager
 * 
 * HTML5 / Web Browser Architecture Notice:
 * The Discord Game SDK and Discord RPC require native IPC communication socket channels
 * which are not supported within standard sandboxed web browsers.
 * 
 * In this HTML5 prototype build, Discord Rich Presence is intentionally disabled.
 * The methods below log activity updates and operate cleanly as safe no-ops.
 * 
 * FUTURE ELECTRON DESKTOP INTEGRATION:
 * When wrapping this project in Electron:
 * 1. Install `discord-rpc` or the official `@discordjs/core` / Discord Game SDK C/C++ bindings in the main Electron process.
 * 2. In `initialize()`, connect to Discord RPC using `discordConfig.applicationId`.
 * 3. Replace the `updatePresence`, `clearPresence`, and `shutdown` stub methods with IPC bridge calls (`ipcRenderer.send('UPDATE_PRESENCE', ...)`).
 */
export class DiscordRichPresenceManager {
  private static isInitialized = false;
  private static currentState: string = discordConfig.defaultPresence.state;
  private static currentDetails: string = discordConfig.defaultPresence.details;

  /**
   * Initializes the Discord Rich Presence system.
   * For Browser: Logs initialization status.
   * For Electron (Future): Establishes connection to Discord RPC client socket.
   */
  public static initialize(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    console.log('[Discord RPC] Initializing Rich Presence with App ID:', discordConfig.applicationId);

    const win = window as any;
    if (win.electronAPI && win.electronAPI.updatePresence) {
      win.electronAPI.updatePresence({
        state: this.currentState,
        details: this.currentDetails,
        largeImageKey: discordConfig.largeImageKey,
        largeImageText: discordConfig.largeImageText,
        smallImageKey: discordConfig.smallImageKey,
        smallImageText: discordConfig.smallImageText,
        startTimestamp: Date.now(),
      });
    }
  }

  /**
   * Alias for initialize() to maintain backward compatibility.
   */
  public static init(): void {
    this.initialize();
  }

  /**
   * Updates the user's Discord Rich Presence activity.
   * @param state Current state message (e.g. "Exploring Ancient Ruins")
   * @param details Current activity details (e.g. "The Forgotten Ruins")
   */
  public static updatePresence(state?: string, details?: string): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    this.currentState = state || discordConfig.defaultPresence.state;
    this.currentDetails = details || discordConfig.defaultPresence.details;

    console.log(`[Discord RPC] Presence Updated -> State: "${this.currentState}", Details: "${this.currentDetails}"`);

    const win = window as any;
    if (win.electronAPI && win.electronAPI.updatePresence) {
      win.electronAPI.updatePresence({
        state: this.currentState,
        details: this.currentDetails,
        largeImageKey: discordConfig.largeImageKey,
        largeImageText: discordConfig.largeImageText,
        smallImageKey: discordConfig.smallImageKey,
        smallImageText: discordConfig.smallImageText,
        startTimestamp: Date.now(),
      });
    }
  }

  /**
   * Clears the current Discord Rich Presence activity.
   */
  public static clearPresence(): void {
    this.currentState = '';
    this.currentDetails = '';
    console.log('[Discord RPC] Presence Cleared.');

    const win = window as any;
    if (win.electronAPI && win.electronAPI.clearPresence) {
      win.electronAPI.clearPresence();
    }
  }

  /**
   * Shuts down the Discord Rich Presence connection gracefully.
   * For Browser: Sets initialization state to false.
   * For Electron (Future): Destroys the RPC socket client connection.
   */
  public static shutdown(): void {
    this.clearPresence();
    this.isInitialized = false;
    console.log('[Discord RPC Web Stub] System Shutdown.');

    /* -------------------------------------------------------------------------- */
    /* FUTURE ELECTRON IMPLEMENTATION PLACEHOLDER                                 */
    /* -------------------------------------------------------------------------- */
    /*
      if (window.electronAPI) {
        window.electronAPI.send('DISCORD_RPC_SHUTDOWN');
      }
    */
  }
}
